import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ToastConfig, ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [MatIconModule],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent implements OnInit, OnDestroy {
  private readonly toastService = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly popoverRef = viewChild<ElementRef<HTMLElement>>('popover');
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  readonly visible = signal(false);
  readonly dismissing = signal(false);
  readonly text = signal('');
  readonly isDismissable = signal(false);
  readonly type = signal<'default' | 'error'>('default');
  readonly offsetY = signal('24px');
  readonly centerX = signal('50%');

  ngOnInit(): void {
    this.toastService.toast$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((config) => this.showToast(config));
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.hidePopover();
  }

  dismiss(): void {
    if (!this.visible() || this.dismissing()) {
      return;
    }

    this.dismissing.set(true);
    this.clearTimer();
    this.dismissTimer = setTimeout(() => {
      this.visible.set(false);
      this.dismissing.set(false);
      this.hidePopover();
    }, 500);
  }

  private showToast(config: ToastConfig): void {
    this.clearTimer();
    this.dismissing.set(false);
    this.text.set(config.text);
    this.isDismissable.set(config.isDismissable);
    this.type.set(config.type ?? 'default');
    this.offsetY.set(config.offsetY ?? '24px');
    this.centerX.set(config.centerX ?? '50%');
    this.visible.set(true);
    this.changeDetector.detectChanges();
    this.hidePopover();
    this.popoverRef()?.nativeElement.showPopover?.();

    if (config.duration > 0) {
      this.dismissTimer = setTimeout(() => this.dismiss(), config.duration);
    }
  }

  private hidePopover(): void {
    this.popoverRef()?.nativeElement.hidePopover?.();
  }

  private clearTimer(): void {
    if (this.dismissTimer !== null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }
}